import cn from "classnames";
import moment from "moment";
import React, { useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Container, Row } from "reactstrap";
import { useAdvertisementCore } from "../../cores/advertisements";
import { useFacebookAdsCore } from "../../cores/facebookAds";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { useIntl } from "react-intl";
import AudienceForm from "../component/advertisements/AudienceForm";
import BudgetForm from "../component/advertisements/BudgetForm";
import DemographyForm from "../component/advertisements/DemographyForm";
import DesignForm from "../component/advertisements/DesignForm";
import CampaignReview from "../component/advertisements/modal/CampaignReview";
import StartForm from "../component/advertisements/StartForm";
import { PanelWembii } from "../component/PanelWembii";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import SideModal from "../layout/SideModal";
import { NoData } from "../component/NoData";
import "./FacebookCampaigns.scss";
import "./CreateCampaign.scss";
import { CampaignGoals } from "../../cores/advertisements/constants/campaign-goals";
import FormsForm from "../component/advertisements/FormsForm";
import { isUrl } from "../utils/stringUtils";
import { CAMPAIGN_REVIEW } from "../../constants/modal.constant";
import { useGlobalCore } from "../../cores/globals";

export default function CreateCampaign() {
  const intl = useIntl();
  const { company, companyPlan, companyRole, isAdmin, isReseller } = useMeCore();
  const { goToFacebookCampaigns, goToSocial, goToBinding } = useRouterCore();
  const {
    campaign,
    image,
    minBudget,
    deliveryEstimate,
    getDeliveryEstimate,
    getMinBudget,
    postCampaign,
    reset,
  } = useAdvertisementCore();
  const {
    turnOffModal,
    turnOnModal,
    getModalVisibility,
    isModal,
    isDarkMode,
  } = useGlobalCore();
  const { settings } = useFacebookAdsCore();

  useEffect(() => {
    if (
      !companyPlan?.rules.allowFacebookAdvertisements ||
      (!companyRole?.ads &&
        !companyRole?.owner &&
        !companyRole?.admin &&
        !companyRole?.agent &&
        !isAdmin &&
        !isReseller)
    )
      goToSocial();
  }, [companyPlan, companyRole, isAdmin, isReseller, goToSocial]);

  useEffect(() => {
    if (!company?.id || !settings?.accountId) return;
    getMinBudget(company.id, settings.accountId, settings.currency);
  }, [company, getMinBudget, settings]);

  const toggleSideModal = useCallback(() => {
    const minImp = minBudget?.minDailyBudgetImp;
    if (minImp != null && campaign.dailyBudget < minImp) {
      toast.error(
        `${intl.formatMessage({ id: "app.toast.error64" })} ${minImp}`
      );
      return;
    }
    if (
      campaign.endTime &&
      moment(campaign.endTime).isBefore(moment(campaign.startTime).add(1, "d"))
    ) {
      toast.error(intl.formatMessage({ id: "app.toast.error65" }));
      return;
    }
    if (
      deliveryEstimate != null &&
      deliveryEstimate.estimateDau <= 2000
    ) {
      toast.error(intl.formatMessage({ id: "app.toast.error66" }));
      return;
    }

    turnOnModal(CAMPAIGN_REVIEW, true);
  }, [turnOnModal, minBudget, campaign, deliveryEstimate, intl]);

  const onSubmit = useCallback(async () => {
    const ok = await postCampaign(
      company?.id!,
      settings?.accountId!,
      campaign,
      image.file!
    );
    if (ok) {
      reset();
      turnOffModal(CAMPAIGN_REVIEW);
      goToFacebookCampaigns();
    }
  }, [
    postCampaign,
    company,
    settings,
    campaign,
    image.file,
    reset,
    goToFacebookCampaigns,
    turnOffModal,
  ]);

  useEffect(() => {
    if (!company?.id || !settings?.accountId) return;
    if (campaign.targeting.countries.length) {
      getDeliveryEstimate(
        company.id,
        settings.accountId,
        campaign.objective,
        campaign.targeting
      );
    }
  }, [
    campaign.targeting,
    campaign.objective,
    company,
    getDeliveryEstimate,
    settings,
  ]);

  const formatDeliveryEstimate = useCallback(() => {
    if (
      !deliveryEstimate ||
      (!campaign.targeting.countries.length &&
        !campaign.targeting.cities.length)
    ) {
      return 0;
    }
    if (deliveryEstimate.estimateDau > 1000000) {
      return `${(deliveryEstimate.estimateDau / 1000).toFixed(0)}K`;
    }
    return deliveryEstimate.estimateDau;
  }, [deliveryEstimate, campaign.targeting]);

  const disabled = useCallback(
    () =>
      !campaign.name ||
      !campaign.url ||
      !isUrl(campaign.url) ||
      !campaign.startTime ||
      !campaign.dailyBudget ||
      !campaign.titleBusiness ||
      !campaign.urlBusiness ||
      !isUrl(campaign.urlBusiness) ||
      campaign.targeting.publisherPlatforms.length === 0 ||
      !campaign.description ||
      !image.file ||
      (campaign.targeting.countries.length === 0 &&
        campaign.targeting.cities.length === 0),
    [campaign, image]
  );

  const isFieldToLong = (field: string, length: number) =>
    field.length > length;

  const disabled2 = useCallback(
    () =>
      campaign.objective === CampaignGoals.LEAD_GENERATION.id &&
      (!campaign.form.name ||
        !campaign.form.contextCard.title ||
        isFieldToLong(campaign.form.contextCard.title, 60) ||
        !campaign.form.contextCard.content ||
        !campaign.form.privacyPolicy.linkText ||
        isFieldToLong(campaign.form.privacyPolicy.linkText, 70) ||
        !campaign.form.privacyPolicy.url ||
        !isUrl(campaign.form.privacyPolicy.url) ||
        !campaign.form.thankYouPage.title ||
        isFieldToLong(campaign.form.thankYouPage.title, 60) ||
        !campaign.form.thankYouPage.body ||
        !campaign.form.thankYouPage.buttonText ||
        isFieldToLong(campaign.form.thankYouPage.buttonText, 60) ||
        !campaign.form.thankYouPage.websiteUrl ||
        !isUrl(campaign.form.thankYouPage.websiteUrl) ||
        !campaign.form.thankYouPage.title ||
        (campaign.form.autoQuestions.length === 0 &&
          campaign.form.customQuestions.length === 0)),
    [campaign]
  );

  const noAdAccount = !settings?.accountId;

  return (
    <Layout>
      <Header2
        title={intl.formatMessage({ id: "app.advertisements.createCampaing" })}
        subtitle={
          settings?.name
            ? intl.formatMessage(
                { id: "app.advertisements.createCampaignWizard.accountLabel" },
                { name: settings.name }
              )
            : undefined
        }
        bottomChildren={
          <button
            type="button"
            className="fb-create-campaign__back"
            onClick={goToFacebookCampaigns}
          >
            <FontAwesomeIcon icon={["fas", "angle-left"]} />
            {intl.formatMessage({ id: "app.advertisements.goBack" })}
          </button>
        }
        rightChildren={
          !noAdAccount ? (
            <span className="fb-create-campaign__scope-pill">
              {intl.formatMessage(
                { id: "app.advertisements.scope" },
                { quantity: formatDeliveryEstimate() }
              )}
            </span>
          ) : null
        }
      />
      <Content>
        <Container fluid className="px-md-3">
          <div
            className={cn("fb-campaigns-ai fb-create-campaign", {
              "fb-campaigns-ai--dark": isDarkMode,
            })}
          >
            {noAdAccount ? (
              <PanelWembii
                shadow
                className="fb-campaigns-ai__panel p-4 p-md-5 mb-4"
              >
                <NoData
                  image="generic6"
                  text={intl.formatMessage({
                    id: "app.advertisements.createCampaignWizard.noAdAccount",
                  })}
                  actionLabel={intl.formatMessage({
                    id: "app.advertisements.createCampaignWizard.goToConnections",
                  })}
                  action={goToBinding}
                  horizontal
                />
              </PanelWembii>
            ) : (
              <>
                <p className="fb-create-campaign__intro">
                  {intl.formatMessage({
                    id: "app.advertisements.createCampaignWizard.hint",
                  })}
                </p>
                <div className="fb-create-campaign__forms">
                  <Row>
                    <Col lg="6" md="12">
                      <StartForm />
                    </Col>
                    <Col lg="6" md="12">
                      <DemographyForm />
                      <AudienceForm />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg="9" md="12">
                      <DesignForm />
                    </Col>
                    <Col lg="3" md="12">
                      <BudgetForm />
                    </Col>
                  </Row>
                  {campaign.objective === CampaignGoals.LEAD_GENERATION.id && (
                    <FormsForm />
                  )}
                </div>
                <PanelWembii
                  shadow
                  className="fb-campaigns-ai__panel fb-create-campaign__footer p-4 mb-4"
                >
                  <div className="fb-create-campaign__footer-inner">
                    <Button
                      type="button"
                      className="fb-campaigns-ai__cta"
                      onClick={toggleSideModal}
                      disabled={disabled() || disabled2()}
                    >
                      {intl.formatMessage({
                        id: "app.advertisements.createCampaing",
                      })}
                    </Button>
                    <p className="fb-create-campaign__footer-hint">
                      {intl.formatMessage({
                        id: "app.advertisements.createCampaignWizard.reviewHint",
                      })}
                    </p>
                  </div>
                </PanelWembii>
              </>
            )}
          </div>
        </Container>
      </Content>
      <SideModal
        active={isModal(CAMPAIGN_REVIEW)}
        visible={getModalVisibility(CAMPAIGN_REVIEW)}
        onClose={() => {
          turnOffModal(CAMPAIGN_REVIEW);
        }}
      >
        <CampaignReview onSubmit={onSubmit} />
      </SideModal>
    </Layout>
  );
}
